import { ref } from 'vue';
import HoldingRepository from '@/repositories/holding/HoldingRepository.ts';
import {
    Holding, HoldingCreateForm,
    HoldingPaginatorItem,
    HoldingsPaginator,
    HoldingsRequestFilter, HoldingUpdateForm,
} from '@/types/holding.type.ts';
import { PaginationMeta } from '@/types/pagination-meta.ts';
import { DealerListItem } from '@/types/dealer.type.ts';
import useLoading from '@/utils/useLoading.ts';

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export default function useHoldingRepository() {
    const arHoldings = ref<HoldingPaginatorItem[]>([]);
    const holdingsPaginationMeta = ref<PaginationMeta | null>(null);
    const { isLoading: isHoldingsLoading, setLoading: setHoldingsLoading } = useLoading();
    const holdingRepository = new HoldingRepository();

    const getHoldings = (requestFilter?: HoldingsRequestFilter): void => {
        setHoldingsLoading(true);

        holdingRepository.getHoldings(requestFilter)
            .then((holdingsPaginator: HoldingsPaginator) => {
                arHoldings.value = holdingsPaginator.data;
                holdingsPaginationMeta.value = holdingsPaginator.meta;
                setHoldingsLoading(false);
            });
    };

    const getHolding = (id: number): Promise<Holding> => {
        return holdingRepository.getHolding(id);
    };

    const createHolding = (holdingCreateForm: HoldingCreateForm): Promise<void> => {
        return holdingRepository.createHolding(holdingCreateForm);
    };

    const updateHolding = (id: number, holdingUpdateForm: HoldingUpdateForm): Promise<void> => {
        return holdingRepository.updateHolding(id, holdingUpdateForm);
    };

    const activateHolding = (id: number): Promise<void> => {
        return holdingRepository.activateHolding(id);
    };

    const deactivateHolding = (id: number): Promise<void> => {
        return holdingRepository.deactivateHolding(id);
    };

    const getAllActiveHoldings = (): Promise<Holding[]> => {
        return holdingRepository.getAllActiveHoldings();
    };

    const getHoldingDealers = (id: number): Promise<DealerListItem[]> => {
        return holdingRepository.getHoldingDealers(id);
    };

    return {
        arHoldings,
        holdingsPaginationMeta,
        isHoldingsLoading,
        getHoldings: getHoldings,
        getHolding: getHolding,
        createHolding: createHolding,
        updateHolding: updateHolding,
        activateHolding: activateHolding,
        deactivateHolding: deactivateHolding,
        getAllActiveHoldings: getAllActiveHoldings,
        getHoldingDealers: getHoldingDealers,
    };
}
